import { HttpClient, HttpHeaders } from '@angular/common/http';

import { Alert } from '../models/alert.models';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { formatDistanceToNow } from 'date-fns';

interface NotificationPushService {
  id: number;
  value: boolean;
}

export interface AlertData {
  title: string;
  use_alert_title_as_subject: boolean;
  deviation_threshold_value: number;
  deviation_period: number;
  deviation_direction: number;
  privacy_id: boolean;
  description: string;
  trigger_type_id: number;
  notification_posts_count: number;
  trigger_new_posts_count: number;
  notification_post_order_type_id: number;
  filters?: any; // Assuming filters is a string, but it might be more complex.
  notification_push_service?: NotificationPushService[];
}

@Injectable({
  providedIn: 'root',
})
export class AlertService {
  constructor(private http: HttpClient) {}

  serializeParams(obj, prefix = '') {
    const pairs = [];

    function encode(value, key) {
      if (value == null) return; // Skip null or undefined values
      if (typeof value === 'object') {
        // Recurse into array or object
        serialize(value, key);
      } else {
        // Encode primitive value and push to pairs array
        pairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
      }
    }

    function serialize(obj, parentKey = '') {
      Object.entries(obj).forEach(([key, value]) => {
        const compositeKey = parentKey ? `${parentKey}[${key}]` : key;
        if (Array.isArray(value)) {
          // Handle array, use index as a key modifier
          value.forEach((val, idx) => encode(val, `${compositeKey}[${idx}]`));
        } else if (typeof value === 'object' && value !== null) {
          // Recurse for nested object
          serialize(value, compositeKey);
        } else {
          // Handle primitive values directly
          encode(value, compositeKey);
        }
      });
    }

    serialize(obj);

    return pairs.join('&');
  }

  formatTimestampToNow(value: string | number | Date) {
    if (!value) return '';
    return formatDistanceToNow(new Date(value), { addSuffix: true });
  }

  fetchAlerts(
    objectClassId: number,
    objectId: string,
  ): Observable<Array<Alert>> {
    const params = {
      object_class_id: objectClassId.toString(),
      object_id: objectId,
    };

    return this.http.get<Array<Alert>>(`/utils_data_object/list_alerts`, {
      params,
    });
  }

  fetchUserAlerts(userId: string): Observable<Array<Alert>> {
    const params = {
      user_id: userId,
    };

    return this.http.get<Array<Alert>>(`/user/${userId}/alerts`, {
      params,
    });
  }

  /**
   * Creates an alert for a specific feed.
   * @param {string} feedId - The ID of the feed.
   * @param {Alert} alert - The alert object to be created.
   * @returns {Observable<Alert>} - An observable that emits the created alert.
   */
  createAlert(feedId: string, alert: AlertData): Observable<Alert> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
    });

    const urlEncodedData = this.serializeParams(alert);

    return this.http.post<Alert>(`/feed/${feedId}/alert`, urlEncodedData, {
      headers: headers,
    });
  }

  /**
   * Edits an alert with the specified ID.
   * @param {string} alertId - The ID of the alert to edit.
   * @param {AlertData} alert - The updated alert data.
   * @returns {Observable<Alert>} - An observable that emits the edited alert.
   */
  editAlert(alertId: string, alert: AlertData): Observable<Alert> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
    });

    const urlEncodedData = this.serializeParams(alert);

    return this.http.post<Alert>(
      `/alert/${alertId}`,
      urlEncodedData.toString(),
      {
        headers: headers,
      },
    );
  }

  deleteAlert(alertId: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
    });

    const url = `/alert/${alertId}`;

    return this.http.delete(url, { headers });
  }

  toggleAlertMode(alertId: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
    });

    const url = `/alert/${alertId}/toggle_mode`;

    return this.http.patch(url, { headers });
  }
}
